import { Link } from 'react-router-dom';
import {
  BatteryCharging,
  Clock,
  MapPin,
  RefreshCw,
  Radio,
  RadioTower,
  Tags,
} from 'lucide-react';
import { deviceDisplayName, deviceToPoint, formatCoords, type Device } from '@mytag/shared';
import { useDashboard, useRefreshAll } from '@/hooks/queries';
import { PageHeader } from '@/components/layout/AppShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  LoadingState,
  StatTile,
} from '@/components/ui/primitives';
import { ErrorState } from '@/components/common/ErrorState';
import { BatteryGauge, StatusBadge } from '@/components/common/indicators';
import { MapView } from '@/components/map/MapView';
import { formatDateTime, formatRelative } from '@/lib/format';

export function DashboardPage(): JSX.Element {
  const { data, isLoading, isError, error, refetch, isFetching } = useDashboard();
  const refreshAll = useRefreshAll();

  if (isLoading) return <LoadingState label="Chargement du tableau de bord…" />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return <EmptyState title="Aucune donnée" />;

  const points = data.devices
    .map((device) => deviceToPoint(device))
    .filter((point): point is NonNullable<typeof point> => point !== null);

  const lastKnown = [...data.devices]
    .filter((device) => device.lastUpdate)
    .sort((a, b) => Date.parse(b.lastUpdate as string) - Date.parse(a.lastUpdate as string))[0];

  return (
    <>
      <PageHeader
        title="Tableau de bord"
        description="Vue d'ensemble des SmartTags liés au compte."
        actions={
          <>
            <Button
              variant="outline"
              icon={<RefreshCw className="h-4 w-4" />}
              loading={isFetching && !refreshAll.isPending}
              onClick={() => void refetch()}
            >
              Recharger
            </Button>
            <Button
              icon={<RefreshCw className="h-4 w-4" />}
              loading={refreshAll.isPending}
              disabled={data.devices.length === 0}
              onClick={() =>
                refreshAll.mutate(data.devices.map((device) => device.deviceNum))
              }
            >
              Tout rafraîchir
            </Button>
          </>
        }
      />

      {refreshAll.isError ? <ErrorState error={refreshAll.error} title="Rafraîchissement partiel" /> : null}
      {refreshAll.isSuccess ? (
        <Alert tone="success" title="Positions rafraîchies">
          {refreshAll.data.results.length} device(s) mis à jour.
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Devices liés"
          value={data.totalDevices}
          hint={
            data.knownDevicesFound.length > 0
              ? `${data.knownDevicesFound.length} device(s) de test détecté(s)`
              : 'Aucun device de test détecté'
          }
          icon={<Tags className="h-4 w-4" />}
          tone="info"
        />
        <StatTile
          label="Connexion fournisseur"
          value={data.onlineDevices}
          hint={`${data.offlineDevices} hors ligne · ${data.unknownDevices ?? 0} non communiqué(s)`}
          icon={data.onlineDevices > 0 ? <Radio className="h-4 w-4" /> : <RadioTower className="h-4 w-4" />}
          tone={data.onlineDevices > 0 ? 'success' : 'neutral'}
        />
        <StatTile
          label="Batterie moyenne"
          value={data.averageBattery !== null ? `${data.averageBattery} %` : '—'}
          hint="Moyenne des devices renvoyant un niveau"
          icon={<BatteryCharging className="h-4 w-4" />}
          tone="warning"
        />
        <StatTile
          label="Dernière position reçue"
          value={data.lastUpdate ? formatRelative(data.lastUpdate) : '—'}
          hint={data.lastUpdate ? formatDateTime(data.lastUpdate) : 'Aucune position reçue'}
          icon={<Clock className="h-4 w-4" />}
        />
      </div>

      {data.knownDevicesFound.length > 0 ? (
        <Alert tone="success" title="Devices de test détectés">
          <div className="flex flex-wrap gap-2 pt-1">
            {data.knownDevicesFound.map((deviceNum) => (
              <Link key={deviceNum} to={`/devices/${deviceNum}`}>
                <Badge tone="info" className="font-mono hover:bg-primary/20">
                  {deviceNum}
                </Badge>
              </Link>
            ))}
          </div>
        </Alert>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>Dernières positions connues</CardTitle>
          </CardHeader>
          <CardContent>
            {points.length > 0 ? (
              <MapView points={points} height="380px" />
            ) : (
              <EmptyState
                icon={<MapPin className="h-5 w-5" />}
                title="Aucune position disponible"
                description="Lancer « Tout rafraîchir » pour demander une position aux tags."
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dernière position reçue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {lastKnown ? <DeviceSummary device={lastKnown} /> : <p className="text-sm text-muted-foreground">—</p>}
            <div className="space-y-2 border-t border-border pt-3">
              {data.devices.slice(0, 6).map((device) => (
                <Link
                  key={device.deviceNum}
                  to={`/devices/${device.deviceNum}`}
                  className="focus-ring flex items-center justify-between gap-3 rounded-md p-2 hover:bg-muted"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{deviceDisplayName(device)}</span>
                    <span className="block font-mono text-[11px] text-muted-foreground">
                      {device.deviceNum}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <BatteryGauge level={device.battery} />
                    <StatusBadge status={device.status} />
                  </span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function DeviceSummary({ device }: { device: Device }): JSX.Element {
  return (
    <div className="space-y-2 rounded-md bg-muted/50 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">{deviceDisplayName(device)}</span>
        <StatusBadge status={device.status} />
      </div>
      <p className="font-mono text-xs text-muted-foreground">{device.deviceNum}</p>
      <p className="tabular font-mono text-xs">{formatCoords(device.latitude, device.longitude)}</p>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <BatteryGauge level={device.battery} />
        <span>{formatRelative(device.lastUpdate)}</span>
      </div>
    </div>
  );
}
